import { TreeNode, TreeNodeType } from "~/components/build/Show/components/Sidebar/TreeNode";
import { JobLabel } from "~/components/build/Show/components/Sidebar/TreeNode/JobLabel";
import { StepLink } from "~/components/build/Show/components/Sidebar/TreeNode/StepLink";
import { isJobFailed } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { CommandJob } from "~/components/build/Show/lib/types";
import { isJobIgnored } from "~/components/build/Show/lib/isJobIgnored";
import { resolveJobUrl } from "~/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { CommandStep } from "~/lib/pipeline";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { jobQueryOptions } from "~/components/build/Show/lib/queries/job";

interface CommandJobProps {
  step: CommandStep;
  job: CommandJob;
  depth: number;
}

export function CommandJobItem({ step, job, depth }: CommandJobProps) {
  const { build } = useBuild();
  const params = useQueryParams();
  const { currentLatestJobUuid } = useCurrentJobOrStep();
  const queryClient = useQueryClient();

  const handleMouseEnter = useCallback(() => {
    if (Features.ProgressiveBuildShow) {
      queryClient.prefetchQuery(jobQueryOptions(build, job.id));
    }
  }, [queryClient, build.id, job.id]);

  return (
    <StepLink
      state={step.state}
      type={step.type}
      to={resolveJobUrl(job.id, { open: !isJobIgnored(job) })}
      onMouseEnter={handleMouseEnter}
      active={
        job.id === params.jobUuid || (job.id === currentLatestJobUuid && Boolean(params.jobUuid))
      }
      depth={depth}
      failed={isJobFailed(job, build)}
      className={isJobIgnored(job) ? "cursor-not-allowed opacity-60" : ""}
      disabled={isJobIgnored(job)}
      title={isJobIgnored(job) ? job.tooltip || "Skipped jobs cannot be viewed" : undefined}
    >
      <JobLabel job={job} step={step} />
    </StepLink>
  );
}

export const createCommandJobNode = (step: CommandStep, job: CommandJob): TreeNode => ({
  id: job.id,
  type: TreeNodeType.JobCommand,
  render: (props) => <CommandJobItem step={step} job={job} {...props} />,
});
